// src/components/covers/CoverCard.jsx
import { Box, Typography, Chip, Stack } from '@mui/material';
import { API_BASE } from '../../utils/api';

const NOT_FOUND = `${API_BASE}/static/images/book-covers/notFound.jpg`;

const resolveCoverSrc = (val) => {
  const v = (val || '').trim();
  if (!v) return NOT_FOUND;
  if (v.startsWith('http://') || v.startsWith('https://')) return v;
  if (v.startsWith('/static/')) return `${API_BASE}${v}`;
  if (v.startsWith('/')) return `${API_BASE}${v}`;
  return `${API_BASE}/static/images/book-covers/${v}`;
};

const formatPrice = (cents, currency = 'usd') => {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
};

/**
 * Props:
 *  - cover: { id|_id, title, designerName, coverType, cover_url, price_cents, currency, tags, exclusive, soldOut }
 *  - onCardClick?: (cover) => void
 */
export default function CoverCard({ cover, onCardClick }) {
  const coverValue = cover.cover_url || cover.coverImage;
  const coverSrc = resolveCoverSrc(coverValue);

  const wrapperProps = onCardClick
    ? {
        component: 'div',
        role: 'button',
        tabIndex: 0,
        onClick: () => onCardClick(cover),
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick(cover);
          }
        },
      }
    : {};

  return (
    <Box
      {...wrapperProps}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: '240px',
        minWidth: '200px',
        textDecoration: 'none',
        cursor: onCardClick ? 'pointer' : 'default',
        '&:hover img': { transform: 'scale(1.03)', boxShadow: 2 },
        position: 'relative',
      }}
    >
      {/* Sold Out badge */}
      {cover.soldOut && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: '#e53e3e',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            px: 1,
            py: 0.3,
            borderRadius: '4px',
            zIndex: 2,
          }}
        >
          SOLD
        </Box>
      )}

      {/* Exclusive badge */}
      {cover.exclusive && !cover.soldOut && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: '#D4AF37',
            color: '#1A1A1A',
            fontSize: '11px',
            fontWeight: 700,
            px: 1,
            py: 0.3,
            borderRadius: '4px',
            zIndex: 2,
          }}
        >
          EXCLUSIVE
        </Box>
      )}

      {/* Cover Image */}
      <Box
        component="img"
        src={coverSrc}
        alt={cover.title || 'Cover design'}
        onError={(e) => { e.currentTarget.src = NOT_FOUND; }}
        sx={{
          width: '100%',
          height: '260px',
          objectFit: 'cover',
          borderRadius: 1,
          transition: 'all 0.3s ease',
          mb: 1,
        }}
      />

      {/* Meta */}
      <Box sx={{ p: 1, flexGrow: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {cover.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          by {cover.designerName}
        </Typography>

        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          {cover.coverType && (
            <Chip label={cover.coverType} size="small" color="secondary" sx={{ color: '#1A1A1A', fontSize: '11px' }} />
          )}
          {(cover.tags || []).slice(0, 2).map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '11px' }} />
          ))}
        </Stack>

        <Typography
          variant="subtitle2"
          sx={{ mt: 1, fontWeight: 700, color: '#413B8D' }}
        >
          {formatPrice(cover.price_cents, cover.currency)}
        </Typography>
      </Box>
    </Box>
  );
}
